import { useCallback, useEffect, useState } from "react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { usePageBack } from "../context/PageBackContext.js";
import { exactRoleLabel } from "./AttendancePage.js";
import { Input } from "../components/ui/input.js";
import { Badge } from "../components/ui/badge.js";
import { Pagination } from "../components/ui/pagination.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { Skeleton } from "../components/Skeleton.js";

/** "SCHEDULE" is an assigned standing shift; "TEMPORARY" an approved request from
 *  the employee to work different hours over a period, which never moved it. */
type ShiftChangeKind = "SCHEDULE" | "TEMPORARY";

interface ShiftChangeEntry {
  kind?: ShiftChangeKind;
  id: string;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  shiftType: string;
  from: string;
  to: string;
  durationHours: number;
  previous: { shiftType: string; from: string; to: string; effectiveFrom: string } | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  setAt: string;
  setBy: { userId: string; name: string | null; role: string | null };
  reason: string | null;
}

interface ChangeLog {
  data: ShiftChangeEntry[];
  meta: { page: number; limit: number; total: number };
}


function decodeRole(token: string): string {
  try {
    return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["role"] ?? "");
  } catch { return ""; }
}

function whenChanged(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/**
 * Org-wide record of every shift assignment: who changed whose shift, from what to
 * what, when it was made and when it takes effect.
 *
 * Super HR admin only, matching the endpoint. The list is ordered by when each change
 * was *made*, not when it takes effect, so a backdated or future-dated shift still
 * appears at the point someone actually assigned it.
 */
export default function ShiftHistoryPage() {
  const { accessToken } = useAuth();
  const role = accessToken ? decodeRole(accessToken) : "";
  const allowed = role === "SUPER_HR_ADMIN";

  usePageBack("/admin", "Back to Admin");

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [log, setLog] = useState<ChangeLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    if (!allowed) { setLoading(false); return; }
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (debounced) params.set("search", debounced);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    setLoading(true);
    setError(null);
    api.get<ChangeLog>(`/shift-history?${params}`)
      .then((r) => setLog(r.data))
      .catch((err) => {
        logError("ShiftHistoryPage.load", err);
        setError(errMsg(err) ?? "Failed to load shift change history");
        setLog(null);
      })
      .finally(() => setLoading(false));
  }, [allowed, page, limit, debounced, from, to]);

  useEffect(load, [load]);

  if (!allowed) {
    return (
      <div className="px-8 py-10 text-[13px] text-surface-600">
        This record is available to the super HR admin only.
      </div>
    );
  }

  const rows = log?.data ?? [];
  const total = log?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="flex flex-col gap-4 px-8 py-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-bold">Shift Change History</h1>
        <p className="text-[13px] text-surface-600">
          Every shift assignment across the org, newest change first.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by employee name or code..."
          className="w-72"
        />
        <span className="flex items-center gap-2 text-[13px] text-surface-600">
          Changed between
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="w-auto" aria-label="Changed from" />
          <span className="text-surface-500">&rarr;</span>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="w-auto" aria-label="Changed to" />
        </span>
        <span className="ml-auto text-[13px] text-surface-600">{total} change{total === 1 ? "" : "s"}</span>
      </div>

      {error && (
        <div className="rounded-md bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">{error}</div>
      )}

      <div className="overflow-x-auto rounded-md border border-surface-200">
        {loading ? (
          <div className="p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-3 border-b border-surface-100 py-2.5 last:border-b-0">
                <Skeleton width={170} height={14} />
                <Skeleton width={150} height={14} />
                <Skeleton width={120} height={14} />
                <Skeleton width="100%" height={14} />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-surface-500">No shift changes match these filters.</div>
        ) : (
          <Table className="w-max min-w-full">
            <TableHeader>
              <TableRow className="bg-surface-50">
                <TableHead className="whitespace-nowrap">Employee</TableHead>
                <TableHead className="whitespace-nowrap">Change</TableHead>
                <TableHead className="whitespace-nowrap">Effective From</TableHead>
                <TableHead className="whitespace-nowrap">Changed By</TableHead>
                <TableHead className="whitespace-nowrap">Changed At</TableHead>
                <TableHead className="whitespace-nowrap">Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-surface-50">
                  <TableCell>
                    <div className="font-medium">{r.employee.firstName} {r.employee.lastName}</div>
                    <div className="text-[11px] text-surface-500">{r.employee.employeeCode}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {/* The previous window is shown struck through beside the new one, so a
                        reviewer sees what actually changed without opening another row. */}
                    {r.previous && (
                      <span className="mr-2 text-[12px] text-surface-400 line-through">
                        {r.previous.from}&ndash;{r.previous.to}
                      </span>
                    )}
                    <span className="font-semibold">{r.from}&ndash;{r.to}</span>
                    <span className="ml-2 text-[11px] text-surface-500">{r.durationHours}h &middot; {r.shiftType}</span>
                    {r.kind === "TEMPORARY"
                      ? <Badge tone="warning" className="ml-2">Temporary</Badge>
                      : !r.previous && <Badge tone="info" className="ml-2">First shift</Badge>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-[12px]">
                    {/* A temporary period has an end; a standing shift runs until the
                        next one replaces it, so only the start is meaningful there. */}
                    {r.effectiveTo && r.effectiveTo !== r.effectiveFrom
                      ? `${r.effectiveFrom} → ${r.effectiveTo}`
                      : r.effectiveFrom}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="text-[13px]">{r.setBy.name ?? "Unknown"}</div>
                    <div className="text-[11px] text-surface-500">{exactRoleLabel(r.setBy.role) ?? "—"}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-[12px] text-surface-600">{whenChanged(r.setAt)}</TableCell>
                  <TableCell className="max-w-[260px] truncate text-[12px] italic text-surface-600" title={r.reason ?? undefined}>
                    {r.reason ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Pagination
        meta={{ total, page, limit, totalPages }}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
        className="mt-0"
      />
    </div>
  );
}
